
import { Injectable, signal, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import { CHATBOT_SOCKET_EVENTS, CHATBOT_SOCKET_EVENTS_GOOGLE_MEET_INHOUSE, CHATBOT_SOCKET_EVENTS_VOCAL_TRANS_ENG } from '../../pages/public/chatbot/chatbot-socket.events';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { prismNewSocketOptions } from './prism-new-socket';
import { PrismNewSocketReauth } from './prism-new-socket-reauth';

export interface VocalTransEngSocketHandlers {
  onCallStreamStart?: (data: unknown) => void;
  onTranscriptReceived?: (data: unknown) => void;
  onCallEnd?: (data: unknown) => void;
  onTranscriptSummary?: (data: unknown) => void;
  onError?: (data: unknown) => void;
}

@Injectable({ providedIn: 'root' })
export class ChatSocketService {
  socket!: Socket;
  url = this.resolveSocketUrl();
  private readonly apiService = inject(ApiService);
  private readonly authService = inject(AuthService);

  private vocalTransEngHandlers: VocalTransEngSocketHandlers | null = null;
  private vocalTransEngProductPid: number | null = null;
  private vocalTransEngCallRoom: string | null = null;
  private joinedVocalTransEngRooms = new Set<string>();
  transcriptionStatus = signal<'PAUSED' | 'RESUMED'>('RESUMED');
  meetingStatus = signal<'INITIAL' | 'WAITING' | 'CONNECTED'>(
    (typeof window !== 'undefined' && localStorage.getItem('chatbot_meeting_status') as any) || 'INITIAL'
  );
  messages = signal<any[]>(
    (typeof window !== 'undefined' && JSON.parse(localStorage.getItem('chatbot_messages') || 'null')) || []
  );
  isTyping = signal(false);
  conversationStarted = signal(false);

  // Shared Context Data
  botId = signal<string>(
    (typeof window !== 'undefined' && localStorage.getItem('chatbot_bot_id')) || ''
  );
  roomId = signal<string>(
    (typeof window !== 'undefined' && localStorage.getItem('chatbot_room_id')) || ''
  );
  conversationId = signal<string>(
    (typeof window !== 'undefined' && localStorage.getItem('chatbot_conversation_id')) || ''
  );
  sessionId = signal<string>(
    (typeof window !== 'undefined' && localStorage.getItem('chatbot_session_id')) || ''
  );
  sessionStartTab = signal<'ai-assistant' | 'google-meet' | null>(null);

  manualQueryResponse = signal<any>(null);
  manualQueryError = signal<any>(null);
  
  conversationConfig = signal<any>(
    (typeof window !== 'undefined' && JSON.parse(localStorage.getItem('chatbot_conversation_config') || 'null')) || null
  ); // Captured from postMessage
  
  private broadcastChannel = new BroadcastChannel('chatbot_state_sync');
  private configPoller: any = null;

  constructor() {
    this.authService.signedOut$.pipe(takeUntilDestroyed()).subscribe(() => this.disconnect());
    this.setupBroadcastListener();
    this.setupWindowListener();
    this.startConfigPolling();

    // Effect to sync session data to localStorage
    effect(() => {
      if (typeof window !== 'undefined') {
        localStorage.setItem('chatbot_meeting_status', this.meetingStatus());
        localStorage.setItem('chatbot_bot_id', this.botId());
        localStorage.setItem('chatbot_room_id', this.roomId());
        localStorage.setItem('chatbot_conversation_id', this.conversationId());
        localStorage.setItem('chatbot_session_id', this.sessionId());
        localStorage.setItem('chatbot_messages', JSON.stringify(this.messages()));
        if (this.conversationConfig()) {
          localStorage.setItem('chatbot_conversation_config', JSON.stringify(this.conversationConfig()));
        }
      }
    });

    // Clear session from localStorage on actual page reload/navigation.
    // NOTE: beforeunload fires on the iframe when the PAGE reloads (F5, tab close),
    // but does NOT fire when the parent just removes the iframe from DOM.
    // This lets us distinguish the two cases and only restore session for parent-close/reopen.
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.clearSessionFromStorage();
      });
    }
  }

  private clearSessionFromStorage() {
    localStorage.removeItem('chatbot_meeting_status');
    localStorage.removeItem('chatbot_bot_id');
    localStorage.removeItem('chatbot_room_id');
    localStorage.removeItem('chatbot_conversation_id');
    localStorage.removeItem('chatbot_session_id');
    localStorage.removeItem('chatbot_messages');
    localStorage.removeItem('chatbot_conversation_config');
  }

  private setupWindowListener() {
    if (typeof window !== 'undefined') {
      window.addEventListener('message', (event) => {
        if (event.data?.source?.includes('devtools')) return;

        if (event.data?.type === 'CONVERSATION_CONFIG') {
          const newPayload = event.data.payload;
          const currentConfig = this.conversationConfig();
          
          console.log('[SocketService] Received CONVERSATION_CONFIG via window message:', newPayload);

          // Deduplicate to prevent broadcast storms
          if (JSON.stringify(newPayload) !== JSON.stringify(currentConfig)) {
            this.conversationConfig.set(newPayload);
            
            // Broadcast to other iframe that config was received
            this.broadcastChannel.postMessage({ 
              type: 'CONFIG_RECEIVED', 
              payload: newPayload 
            });
            
            // Stop polling once config is received
            if (this.configPoller) {
              console.log('[SocketService] Stopping config poller - config received');
              clearInterval(this.configPoller);
              this.configPoller = null;
            }
          }
        }
      });
    }
  }

  private startConfigPolling() {
    // Listen for config received from other iframe
    this.broadcastChannel.onmessage = (ev) => {
      if (ev.data.type === 'CONFIG_RECEIVED') {
        const newPayload = ev.data.payload;
        if (JSON.stringify(newPayload) !== JSON.stringify(this.conversationConfig())) {
          this.conversationConfig.set(newPayload);
        }
        
        // Stop our polling if the other iframe got the config
        if (this.configPoller) {
          clearInterval(this.configPoller);
          this.configPoller = null;
        }
      } else if (ev.data.type === 'POLLING_STARTED') {
        // Another iframe started polling, don't start our own
        return;
      }
    };

    // Request config from parent if not received within reasonable time
    setTimeout(() => {
      if (!this.conversationConfig()) {
        console.log('[SocketService] Initial config missing, starting poller...');
        // Notify other iframe we're starting to poll
        this.broadcastChannel.postMessage({ type: 'POLLING_STARTED' });
        
        // Use '*' for local dev safety, or environment-specific URL
        const targetOrigin = window.location.hostname === 'localhost' ? '*' : environment.product_postMessage.DENEFITS.server_url;
        window.parent.postMessage({ type: 'REQUEST_CONFIG' }, targetOrigin);
        
        // Keep trying every 3 seconds until we get it
        this.configPoller = setInterval(() => {
          if (this.conversationConfig()) {
            clearInterval(this.configPoller);
            this.configPoller = null;
          } else {
            window.parent.postMessage({ type: 'REQUEST_CONFIG' }, targetOrigin);
          }
        }, 3000);
      }
    }, 1500);
  }

  connect(): void {
    if (this.socket) {
      if (!this.socket.connected) {
        this.socket.connect();
      }
      this.ensureVocalTransEngReady();
      return;
    }

    const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
    const socketUrl = isLocalhost ? environment.socketUrl : this.url;
    // prism-new serves the socket on its own path and authenticates the handshake with the
    // session bearer; the old API's socket sat behind /api/socket.io and trusted the cookie.
    const prismNew = environment.prismNew.enabled
      ? prismNewSocketOptions(() => this.apiService.getAccessToken())
      : null;
    const socketPath = prismNew?.path ?? (isLocalhost ? '/socket.io/' : '/api/socket.io/');
    const socketTransports = isLocalhost ? ['websocket'] : ['polling', 'websocket'];

    this.socket = io(socketUrl, {
      transports: socketTransports,
      path: socketPath,
      ...(prismNew ? { auth: prismNew.auth } : {}),
      withCredentials: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
    });
    if (prismNew) {
      new PrismNewSocketReauth(this.socket, () => this.apiService.refreshAccessToken(), 3).attach();
    }

    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket.id);

      if (this.meetingStatus() === 'CONNECTED' && this.roomId()) {
        console.log('[Socket] Rejoining room after connect:', this.roomId());
        this.joinRoom(this.roomId());
        this.attachStreamListeners();
      }

      this.ensureVocalTransEngReady();
    });

    this.socket.on('disconnect', () => {
      console.info('Socket disconnected');
      this.joinedVocalTransEngRooms.clear();
    });
    this.socket.on('error', (e) => console.error('Socket error', e));

    this.socket.on('manual.query.response', (data) => {
      this.manualQueryResponse.set(data);
      if (data?.roomId) this.joinRoom(data.roomId);
    });

    this.socket.on('manual.query.error', (error) => {
      this.manualQueryError.set(error);
    });
  }

  private resolveSocketUrl(): string {
    if (typeof window === 'undefined') {
      return environment.socketUrl;
    }
    return window.location.hostname === 'localhost'
      ? window.location.origin
      : environment.socketUrl;
  }

  joinRoom(roomId: string) {
    if (!roomId || !this.socket?.connected) return;
    this.socket.emit('room.join', { roomId });
  }

  joinVocalTransEngMonitoring(productPid: number | null): void {
    if (
      this.vocalTransEngProductPid != null &&
      productPid != null &&
      this.vocalTransEngProductPid !== productPid
    ) {
      this.joinedVocalTransEngRooms.delete(
        `room:prism-main-room:product:${this.vocalTransEngProductPid}`
      );
    }
    this.vocalTransEngProductPid = productPid;
    this.ensureVocalTransEngRoomsJoined();
  }

  joinVocalTransEngCallRoom(conversationPid: number, traceId: string): void {
    const nextRoom = `room:vocal-trans-eng:${conversationPid}:${traceId}`;
    if (this.vocalTransEngCallRoom && this.vocalTransEngCallRoom !== nextRoom) {
      this.joinedVocalTransEngRooms.delete(this.vocalTransEngCallRoom);
    }
    this.vocalTransEngCallRoom = nextRoom;
    this.ensureVocalTransEngRoomsJoined();
  }

  clearVocalTransEngCallRoom(): void {
    if (this.vocalTransEngCallRoom) {
      this.joinedVocalTransEngRooms.delete(this.vocalTransEngCallRoom);
    }
    this.vocalTransEngCallRoom = null;
  }

  private ensureVocalTransEngReady(): void {
    if (this.vocalTransEngHandlers) {
      this.registerVocalTransEngListeners();
      this.ensureVocalTransEngRoomsJoined();
    }
  }

  private ensureVocalTransEngRoomsJoined(): void {
    if (!this.socket?.connected) return;

    const rooms = new Set<string>(['room:prism-main-room:members']);
    if (this.vocalTransEngProductPid != null) {
      rooms.add(`room:prism-main-room:product:${this.vocalTransEngProductPid}`);
    }
    if (this.vocalTransEngCallRoom) {
      rooms.add(this.vocalTransEngCallRoom);
    }

    for (const roomId of rooms) {
      if (this.joinedVocalTransEngRooms.has(roomId)) continue;
      console.debug('[ChatSocket] joining vocal trans eng room', roomId);
      this.joinRoom(roomId);
      this.joinedVocalTransEngRooms.add(roomId);
    }
  }

  sendManualQuery(payload: any) {
    this.socket.emit("manual.query", payload);
  }

  waitForBotJoin() {
    // Listen for the bot to start the call to initialize the session
    this.socket.off(CHATBOT_SOCKET_EVENTS_GOOGLE_MEET_INHOUSE.GOOGLE_MEET_INHOUSE_BOT_CALL_STARTED);
    this.socket.on(CHATBOT_SOCKET_EVENTS_GOOGLE_MEET_INHOUSE.GOOGLE_MEET_INHOUSE_BOT_CALL_STARTED, (data: any) => {
      if (data.botId === this.botId()) {
        this.roomId.set(data.roomId);
        this.joinRoom(data.roomId);
        this.broadcastStatus('CONNECTED');
        this.attachStreamListeners();
      }
    });
  }

  attachStreamListeners() {
    const accumulatedStreamResponses = new Map<string, string>();

    // Setup Transcript Listener
    this.socket.off(CHATBOT_SOCKET_EVENTS_GOOGLE_MEET_INHOUSE.GOOGLE_MEET_INHOUSE_TRANSCRIPT_LIVE);
    this.socket.on(CHATBOT_SOCKET_EVENTS_GOOGLE_MEET_INHOUSE.GOOGLE_MEET_INHOUSE_TRANSCRIPT_LIVE, (data: any) => {
      if (data.botId === this.botId()) {

        const isAI = data.speakerName?.toLowerCase().includes('assistant');
        const isAgent = data.isAgent || data.role === 'agent';

        const from = isAI ? 'ai' : (isAgent ? 'agent' : 'user');

        this.updateMessageList(from, data.transcript, data.aiSuggestionsId, '', data.speakerName);

        this.isTyping.set(from === 'user');
      }
    });

    // Setup AI Response Listener
    this.socket.off(CHATBOT_SOCKET_EVENTS_GOOGLE_MEET_INHOUSE.GOOGLE_MEET_INHOUSE_AI_RESPONSE_STREAM);
    this.socket.on(CHATBOT_SOCKET_EVENTS_GOOGLE_MEET_INHOUSE.GOOGLE_MEET_INHOUSE_AI_RESPONSE_STREAM, (data: any) => {
      if (data.botId === this.botId()) {
        const currentRoomId = this.roomId();
        if (currentRoomId && data.roomId && data.roomId !== currentRoomId) {
          return;
        }

        this.isTyping.set(false);
        this.conversationId.set(data.conversationId);

        if (data.aiSuggestionsId) {
          const currentText = accumulatedStreamResponses.get(data.aiSuggestionsId) || '';
          const updatedText = currentText + data.suggestionStreamResponse;
          accumulatedStreamResponses.set(data.aiSuggestionsId, updatedText);
          this.updateMessageList('ai', updatedText, data.aiSuggestionsId, '', '');
        }
      }
    });
  }

  attachVocalTransEngListeners(handlers: VocalTransEngSocketHandlers): void {
    this.vocalTransEngHandlers = handlers;
    this.ensureVocalTransEngReady();
  }

  detachVocalTransEngListeners(): void {
    this.vocalTransEngHandlers = null;
    this.vocalTransEngProductPid = null;
    this.vocalTransEngCallRoom = null;
    this.joinedVocalTransEngRooms.clear();
    this.unregisterVocalTransEngListeners();
  }

  private registerVocalTransEngListeners(): void {
    if (!this.socket || !this.vocalTransEngHandlers) return;

    const events = CHATBOT_SOCKET_EVENTS_VOCAL_TRANS_ENG;
    const handlers = this.vocalTransEngHandlers;

    this.unregisterVocalTransEngListeners();

    this.socket.on(events.CALL_STREAM_START_OUTPUT, (data: unknown) => {
      console.debug('[ChatSocket] vocalTransEng.callStreamStartOutput', data);
      handlers.onCallStreamStart?.(data);
    });
    this.socket.on(events.TRANSCRIPT_RESPONSE_RECEIVED, (data: unknown) => {
      console.debug('[ChatSocket] vocalTransEng.transcriptResponseReceived', data);
      handlers.onTranscriptReceived?.(data);
    });
    this.socket.on(events.CALL_END_OUTPUT_RECEIVED, (data: unknown) => {
      console.debug('[ChatSocket] vocalTransEng.callEndOutputReceived', data);
      handlers.onCallEnd?.(data);
    });
    this.socket.on(events.TRANSCRIPT_SUMMARY_OUTPUT, (data: unknown) => {
      console.debug('[ChatSocket] vocalTransEng.transcriptSummaryOutput', data);
      handlers.onTranscriptSummary?.(data);
    });
    this.socket.on(events.ERROR, (data: unknown) => {
      console.debug('[ChatSocket] vocalTransEng.error', data);
      handlers.onError?.(data);
    });
  }

  private unregisterVocalTransEngListeners(): void {
    if (!this.socket) return;

    const events = CHATBOT_SOCKET_EVENTS_VOCAL_TRANS_ENG;
    this.socket.off(events.CALL_STREAM_START_OUTPUT);
    this.socket.off(events.TRANSCRIPT_RESPONSE_RECEIVED);
    this.socket.off(events.CALL_END_OUTPUT_RECEIVED);
    this.socket.off(events.TRANSCRIPT_SUMMARY_OUTPUT);
    this.socket.off(events.ERROR);
  }

  updateMessageList(from: 'user' | 'ai' | 'agent', text: string, queryId?: string, convId?: string, clientName?: string) {
    this.messages.update(msgs => {
      const existingIdx = queryId ? msgs.findIndex(m => m.queryId === queryId && m.from === from) : -1;

      if (existingIdx !== -1) {
        return msgs.map((msg, i) => i === existingIdx
          ? { ...msg, text, timestamp: new Date().toISOString(), clientName: clientName || 'You', speakerName: clientName }
          : msg
        );
      }
      return [...msgs, { from, text, queryId, conversationId: convId, timestamp: new Date().toISOString(), clientName: clientName || 'You', speakerName: clientName }];
    });
  }

  broadcast(type: string, payload: any) {
    this.broadcastChannel.postMessage({ type, payload });
  }

  broadcastStatus(status: 'INITIAL' | 'WAITING' | 'CONNECTED') {
    this.meetingStatus.set(status);
    
    if (status === 'INITIAL') {
      this.botId.set('');
      this.roomId.set('');
      this.conversationId.set('');
      this.sessionId.set('');
      this.messages.set([]);
      this.clearSessionFromStorage();
    } else if (typeof window !== 'undefined') {
      localStorage.setItem('chatbot_meeting_status', status);
    }

    this.broadcast('MEETING_STATUS', { 
        status, 
        botId: this.botId(), 
        roomId: this.roomId(), 
        conversationId: this.conversationId() 
    });
  }

  private setupBroadcastListener() {
    this.broadcastChannel.onmessage = (event) => {
      const { type, payload } = event.data;

      if (type === 'MEETING_STATUS') {
        this.meetingStatus.set(payload.status);
        if (payload.botId) this.botId.set(payload.botId);
        if (payload.roomId) this.roomId.set(payload.roomId);
        if (payload.conversationId) this.conversationId.set(payload.conversationId);
      } else if (type === 'TRANSCRIPTION_STATUS') {
        this.transcriptionStatus.set(payload.status);
      } else if (type === 'SESSION_START_TAB') {
        this.sessionStartTab.set(payload.tab);
      }
    };
  }

  updateTranscription(action: 'pause' | 'resume') {
    const event = action === 'pause' ? CHATBOT_SOCKET_EVENTS_GOOGLE_MEET_INHOUSE.GOOGLE_MEET_INHOUSE_PAUSE_TRANSCRIPTION : CHATBOT_SOCKET_EVENTS_GOOGLE_MEET_INHOUSE.GOOGLE_MEET_INHOUSE_RESUME_TRANSCRIPTION;
    const payload = {
      botId: this.botId(),
      conversationId: this.conversationId()
    };

    this.socket.emit(event, payload);

    const newStatus = action === 'pause' ? 'PAUSED' : 'RESUMED';
    this.transcriptionStatus.set(newStatus);

    this.broadcast('TRANSCRIPTION_STATUS', { status: newStatus });
    this.isTyping.set(false);
  }

  joinAuditRoom(auditId: string) {
    if (!this.socket) return;
    const roomId = `room:audit:${auditId}:members`;
    this.socket.emit('room.join', { roomId });
  }

  leaveAuditRoom(auditId: string) {
    if (!this.socket) return;
    const roomId = `room:audit:${auditId}:members`;
    this.socket.emit('room.leave', { roomId });
  }

  // The socket is already authenticated at its handshake; the token never rides in an event.
  registerAuditSession(auditId: string, sessionId: string, firstOpenedAt?: string) {
    if (!this.socket) return;
    this.socket.emit('audit.session.register', { auditId, sessionId, firstOpenedAt });
  }

  deregisterAuditSession(auditId: string, sessionId: string) {
    if (!this.socket) return;
    this.socket.emit('audit.session.deregister', { auditId, sessionId });
  }

  /** Closes the connection and forgets the chat session; used when the user signs out. */
  disconnect(): void {
    // Kept, not dropped: a later connect() reuses it with the next user's token.
    this.socket?.disconnect();
    this.clearSessionFromStorage();
  }
}
